import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout';

export default function MyPackagePage() {
    const { token, user } = useAuth();
    const [profile, setProfile] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const role = user?.role || 'developer';

    useEffect(() => {
        const load = async () => {
            if (!token) return;
            try {
                const data = await api.getUserProfile(token);
                setProfile(data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [token]);

    const pkg = profile?.assignedPackage;
    const expiry = profile?.packageExpiry ? new Date(profile.packageExpiry) : null;
    const startDate = profile?.packageStartDate ? new Date(profile.packageStartDate) : null;
    const now = new Date();

    const daysRemaining = expiry
        ? Math.max(0, Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
        : null; // null = lifetime/unlimited

    const totalDays = (expiry && startDate)
        ? Math.ceil((expiry.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
        : null;

    const progressPct = (daysRemaining !== null && totalDays)
        ? Math.max(0, Math.min(100, (daysRemaining / totalDays) * 100))
        : 100;

    const isExpiringSoon = daysRemaining !== null && daysRemaining <= 7;
    const isExpiring30 = daysRemaining !== null && daysRemaining <= 30;

    const progressColor = daysRemaining === null
        ? 'from-emerald-500 to-teal-400'
        : isExpiringSoon
            ? 'from-rose-500 to-red-400'
            : isExpiring30
                ? 'from-amber-500 to-yellow-400'
                : 'from-emerald-500 to-teal-400';

    const upgradeLink = role === 'developer'
        ? '/developer-packages'
        : role === 'broker'
            ? '/broker-packages'
            : '/owner-packages';

    // Extract features from package categories
    const features: string[] = [];
    let projectQuota: number | '∞' | null = null;
    let propertyQuota: number | '∞' | null = null;

    if (pkg?.categories) {
        pkg.categories.forEach((cat: any) => {
            if (cat.features) features.push(...cat.features);
        });
    }

    features.forEach(feat => {
        const cleanFeat = feat.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|\u2705|\u274C/g, '').trim();
        if (cleanFeat.includes('Projects Allowed:')) {
            const val = cleanFeat.split(':')[1]?.trim();
            if (val?.toLowerCase() === 'unlimited') projectQuota = '∞';
            else projectQuota = parseInt(val) || null;
        }
        if (cleanFeat.includes('Properties Allowed:') || cleanFeat.includes('Property Listings:')) {
            const val = cleanFeat.split(':')[1]?.trim();
            if (val?.toLowerCase() === 'unlimited') propertyQuota = '∞';
            else propertyQuota = parseInt(val) || null;
        }
    });

    return (
        <UserDashboardLayout>
            <div className="max-w-3xl mx-auto py-6 px-4">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-2xl font-black text-[#05144b] flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#05144b] flex items-center justify-center">
                            <i className="fa-solid fa-crown text-amber-400"></i>
                        </div>
                        My Package
                    </h1>
                    <p className="text-slate-500 text-sm mt-2 ml-1">Your current plan details and usage quota</p>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-32">
                        <div className="w-10 h-10 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : !pkg ? (
                    /* No Package */
                    <div className="bg-white rounded-3xl border-2 border-dashed border-slate-200 p-12 text-center">
                        <div className="w-20 h-20 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-4">
                            <i className="fa-solid fa-crown text-slate-300 text-3xl"></i>
                        </div>
                        <h2 className="text-xl font-black text-slate-800 mb-2">No Active Package</h2>
                        <p className="text-slate-500 text-sm mb-6">You don't have any package assigned yet. Contact admin or purchase a plan to get started.</p>
                        <Link
                            to={upgradeLink}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-[#05144b] text-white font-bold rounded-xl hover:bg-black transition-all shadow-lg"
                        >
                            <i className="fa-solid fa-rocket text-amber-400"></i>
                            View Available Packages
                        </Link>
                    </div>
                ) : (
                    <div className="space-y-5">
                        {/* Main Package Card */}
                        <div className="bg-[#05144b] rounded-3xl overflow-hidden shadow-2xl relative">
                            {/* Top decorative strip */}
                            <div className="h-1.5 w-full" style={{ backgroundColor: pkg.color || '#3b82f6' }}></div>

                            <div className="p-6 md:p-8">
                                {/* Package Header */}
                                <div className="flex items-start justify-between gap-4 mb-6">
                                    <div className="flex items-center gap-4">
                                        <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-xl"
                                            style={{ backgroundColor: (pkg.color || '#3b82f6') + '22', border: `2px solid ${pkg.color || '#3b82f6'}44` }}>
                                            <i className={`fa-solid ${pkg.icon || 'fa-box'} text-2xl`} style={{ color: pkg.color || '#3b82f6' }}></i>
                                        </div>
                                        <div>
                                            <h2 className="text-2xl font-black text-white tracking-tight">{pkg.name}</h2>
                                            <p className="text-blue-300 text-sm font-medium mt-0.5">{pkg.tagline || pkg.period || 'Standard Plan'}</p>
                                        </div>
                                    </div>
                                    <span className="shrink-0 px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center gap-1.5">
                                        <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
                                        Active
                                    </span>
                                </div>

                                {/* Duration/Expiry Info */}
                                <div className="mb-6">
                                    {daysRemaining === null ? (
                                        // Lifetime / Unlimited
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500/15 border border-emerald-500/25">
                                                <i className="fa-solid fa-infinity text-emerald-400 text-lg"></i>
                                                <div>
                                                    <p className="text-white font-black text-lg leading-none">Lifetime</p>
                                                    <p className="text-emerald-400 text-[11px] font-semibold mt-0.5">No Expiry • Forever Active</p>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex items-end justify-between mb-2">
                                                <div>
                                                    <p className="text-blue-300 text-[11px] font-bold uppercase tracking-widest mb-1">Days Remaining</p>
                                                    <p className={`text-4xl font-black leading-none ${isExpiringSoon ? 'text-rose-400' : isExpiring30 ? 'text-amber-400' : 'text-white'}`}>
                                                        {daysRemaining}
                                                        <span className="text-lg ml-1 font-semibold opacity-60">days</span>
                                                    </p>
                                                </div>
                                                {isExpiringSoon && (
                                                    <span className="px-3 py-1.5 bg-rose-500/20 text-rose-400 border border-rose-500/30 rounded-lg text-xs font-black uppercase tracking-wider">
                                                        ⚠ Expiring Soon
                                                    </span>
                                                )}
                                            </div>
                                            {/* Progress Bar */}
                                            <div className="h-2.5 w-full rounded-full bg-white/10 overflow-hidden mt-3">
                                                <div
                                                    className={`h-full rounded-full bg-gradient-to-r ${progressColor} transition-all duration-700`}
                                                    style={{ width: `${progressPct}%` }}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Date Info */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="bg-white/5 rounded-xl px-4 py-3">
                                        <p className="text-blue-300 text-[10px] font-bold uppercase tracking-widest mb-1">Start Date</p>
                                        <p className="text-white font-bold text-sm">
                                            {startDate
                                                ? startDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                                                : '—'}
                                        </p>
                                    </div>
                                    <div className="bg-white/5 rounded-xl px-4 py-3">
                                        <p className="text-blue-300 text-[10px] font-bold uppercase tracking-widest mb-1">Expiry Date</p>
                                        <p className={`font-bold text-sm ${!expiry ? 'text-emerald-400' : isExpiringSoon ? 'text-rose-400' : 'text-white'}`}>
                                            {expiry
                                                ? expiry.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                                                : '∞ No Expiry'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Quota Card */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                                        <i className="fa-solid fa-city text-blue-600 text-sm"></i>
                                    </div>
                                    <div>
                                        <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                            Projects Left
                                        </p>
                                    </div>
                                </div>
                                <p className="text-4xl font-black text-[#05144b]">
                                    {projectQuota === '∞' || profile?.remainingProjects >= 999
                                        ? '∞'
                                        : `${profile?.remainingProjects ?? 0} / ${projectQuota ?? '?'}`
                                    }
                                </p>
                                <p className="text-slate-400 text-xs font-medium mt-1">
                                    project listings remaining
                                </p>
                            </div>

                            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                                        <i className="fa-solid fa-tag text-amber-500 text-sm"></i>
                                    </div>
                                    <div>
                                        <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Plan Price</p>
                                    </div>
                                </div>
                                <p className="text-3xl font-black text-[#05144b]">
                                    {pkg.price === '0' || pkg.price === 0 ? 'Free' : `₹${pkg.price}`}
                                </p>
                                <p className="text-slate-400 text-xs font-medium mt-1">{pkg.period || 'One Time'}</p>
                            </div>
                        </div>

                        {/* Features Card */}
                        {features.length > 0 && (
                            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                                <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-[#05144b] flex items-center justify-center">
                                        <i className="fa-solid fa-list-check text-white text-xs"></i>
                                    </div>
                                    <h3 className="font-black text-[#05144b] text-base">Plan Features</h3>
                                </div>
                                <div className="p-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                                        {features.map((feat, i) => (
                                            <div key={i} className="flex items-start gap-2.5">
                                                <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
                                                    <i className="fa-solid fa-check text-emerald-600 text-[9px]"></i>
                                                </div>
                                                <span className="text-slate-600 text-sm font-medium leading-snug">{feat}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Upgrade CTA */}
                        <div className="bg-gradient-to-r from-[#05144b] to-[#4c1d95] rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
                            <div>
                                <h3 className="text-white font-black text-lg">Want More Features?</h3>
                                <p className="text-blue-300 text-sm mt-0.5">Upgrade to a higher plan for more listings and premium features.</p>
                            </div>
                            <Link
                                to={upgradeLink}
                                className="shrink-0 flex items-center gap-2 px-5 py-3 bg-amber-400 text-[#05144b] font-black text-sm rounded-xl hover:bg-amber-300 transition-all shadow-lg active:scale-95"
                            >
                                <i className="fa-solid fa-rocket"></i>
                                Upgrade Plan
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </UserDashboardLayout>
    );
}
